import type { QueryClient } from '@tanstack/react-query';

/**
 * Single source of truth for React Query keys. Using these instead of loose
 * string arrays keeps a key and its invalidation from drifting apart, and lets
 * the invalidation helpers below target exactly what changed instead of blowing
 * away the whole cache on every save.
 *
 * Invalidation matches by prefix, so `queryKeys.journal()` (['journal']) also
 * invalidates the parametrised list keys under it.
 */
export const queryKeys = {
  auth: () => ['auth', 'me'] as const,
  dashboard: () => ['dashboard'] as const,
  settings: () => ['settings'] as const,
  situations: () => ['situations'] as const,
  stage: () => ['stage'] as const,
  treatment: () => ['treatment'] as const,
  money: () => ['money'] as const,
  health: () => ['health'] as const,
  triggers: () => ['triggers'] as const,
  achievements: () => ['achievements'] as const,
  checkinToday: () => ['checkin', 'today'] as const,
  push: () => ['push'] as const,
  pushConfig: () => ['push', 'config'] as const,
  journal: () => ['journal'] as const,
  journalList: (search: string, mood: number | null) => ['journal', search, mood] as const,
  stats: () => ['stats'] as const,
  statsPeriod: (period: string) => ['stats', period] as const,
  calendar: () => ['calendar'] as const,
  calendarMonth: (month: string) => ['calendar', month] as const,
  calendarDay: (localDate: string | null) => ['calendar', 'day', localDate] as const,
  admin: () => ['admin'] as const,
  adminUsers: () => ['admin', 'users'] as const,
  adminStats: () => ['admin', 'stats'] as const,
  adminMissions: () => ['admin', 'missions'] as const,
  adminTriggers: () => ['admin', 'triggers'] as const,
  adminUserMissions: (id: number | undefined) => ['admin', 'user-missions', id] as const,
  adminUserSettings: (id: number | undefined) => ['admin', 'user-settings', id] as const,
};

function invalidate(qc: QueryClient, keys: readonly (readonly unknown[])[]): void {
  for (const queryKey of keys) void qc.invalidateQueries({ queryKey });
}

/**
 * Everything that reflects the user's own tracked activity — invalidate after
 * logging or editing it (a craving, a cigarette, a check-in, a note, a dose…).
 * Deliberately leaves out settings/auth/triggers/admin/push, which that kind of
 * change never touches.
 */
export function invalidateActivity(qc: QueryClient): void {
  invalidate(qc, [
    queryKeys.dashboard(),
    queryKeys.treatment(),
    queryKeys.money(),
    queryKeys.health(),
    queryKeys.achievements(),
    queryKeys.checkinToday(),
    queryKeys.journal(),
    queryKeys.calendar(),
  ]);
}

/**
 * After a settings change that the whole app is derived from (mode, Día D,
 * tobacco, language): the preferences plus everything computed from them.
 */
export function invalidateDerived(qc: QueryClient): void {
  invalidate(qc, [
    queryKeys.settings(),
    queryKeys.auth(),
    queryKeys.dashboard(),
    queryKeys.treatment(),
    queryKeys.money(),
    queryKeys.health(),
  ]);
}
